import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Upload, Pencil, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { recuentosAPI, LUGAR_FLAGS, fmtNum, errorMsg } from '@/lib/recuentos';
import { IngredienteDialog } from './IngredienteDialog';

const Flag = ({ on }) => <span className={`inline-block w-2.5 h-2.5 rounded-full ${on ? 'bg-green-500' : 'bg-slate-200'}`} />;

export const CatalogoTab = () => {
  const [ings, setIngs] = useState([]);
  const [verInactivos, setVerInactivos] = useState(false);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState({ open: false, ing: null });
  const fileRef = useRef(null);

  const load = () => recuentosAPI.ingredientes().then((r) => setIngs(r.data));
  useEffect(() => { load(); }, []);

  const visibles = useMemo(() => {
    const t = search.trim().toLowerCase();
    return ings.filter((i) => (verInactivos || i.activo) && (!t || i.nombre_justo.toLowerCase().includes(t) || i.id_justo.includes(t)));
  }, [ings, verInactivos, search]);

  const toggleActivo = async (i) => {
    try {
      await recuentosAPI.updateIngrediente(i.id, { activo: !i.activo });
      load();
    } catch (e) { toast.error(errorMsg(e, 'Error')); }
  };

  const importar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { data } = await recuentosAPI.importarIngredientes(file);
      toast.success(`Leídos ${data.leidos}: ${data.nuevos} nuevos (sin recuento), ${data.existentes_sin_cambios} ya existían`);
      load();
    } catch (err) { toast.error(errorMsg(err, 'Error al importar')); }
  };

  const importarPlantilla = async () => {
    try {
      const { data } = await recuentosAPI.importarPlantilla();
      toast.success(`Ingredientes Website: ${data.leidos} leídos, ${data.nuevos} nuevos (sin recuento), ${data.existentes_sin_cambios} ya existían`);
      load();
    } catch (err) { toast.error(errorMsg(err, 'Error al importar')); }
  };

  const nextOrden = ings.reduce((m, i) => Math.max(m, i.orden), 0) + 1;

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 w-64" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalogo-buscar-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Switch checked={verInactivos} onCheckedChange={setVerInactivos} data-testid="catalogo-ver-inactivos-switch" /> Ver inactivos
          </label>
          <span className="text-sm text-slate-500" data-testid="catalogo-count">{ings.filter((i) => i.activo).length} activos</span>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={importar} data-testid="catalogo-importar-input" />
          <Button variant="outline" onClick={importarPlantilla} data-testid="catalogo-importar-plantilla-btn"><Upload className="w-4 h-4 mr-1" /> Importar desde plantilla guardada</Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="catalogo-importar-btn"><Upload className="w-4 h-4 mr-1" /> Importar Excel de Justo</Button>
          <Button onClick={() => setDialog({ open: true, ing: null })} className="bg-orange-500 hover:bg-orange-600" data-testid="catalogo-nuevo-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
        </div>
      </div>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm" data-testid="catalogo-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Zona</th>
              <th className="px-3 py-2 text-left">Nombre Justo</th>
              <th className="px-3 py-2 text-left">Unidad</th>
              <th className="px-3 py-2 text-left">Alternativa</th>
              {LUGAR_FLAGS.map((l) => <th key={l.field} className="px-2 py-2 text-center text-xs">{l.label}</th>)}
              <th className="px-3 py-2 text-center">Activo</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {visibles.map((i) => (
              <tr key={i.id} className={`border-t ${i.activo ? '' : 'opacity-50'}`} data-testid={`catalogo-row-${i.id_justo}`}>
                <td className="px-3 py-2 text-slate-400">{i.orden}</td>
                <td className="px-3 py-2 text-slate-500 text-xs">{i.zona}</td>
                <td className="px-3 py-2"><div className="text-slate-800">{i.nombre_justo}</div><div className="font-mono text-[10px] text-slate-400">{i.id_justo}</div></td>
                <td className="px-3 py-2">{i.unidad}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{i.unidad_alternativa ? `${i.unidad_alternativa} = ${fmtNum(i.factor_alternativa)}` : '—'}</td>
                {LUGAR_FLAGS.map((l) => <td key={l.field} className="px-2 py-2 text-center"><Flag on={i[l.field]} /></td>)}
                <td className="px-3 py-2 text-center"><Switch checked={i.activo} onCheckedChange={() => toggleActivo(i)} data-testid={`catalogo-activo-${i.id_justo}`} /></td>
                <td className="px-3 py-2"><Button size="sm" variant="ghost" onClick={() => setDialog({ open: true, ing: i })} data-testid={`catalogo-editar-${i.id_justo}`}><Pencil className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <IngredienteDialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })} ingrediente={dialog.ing} nextOrden={nextOrden} onSaved={load} />
    </div>
  );
};
